import React, { JSX, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, AxiosError } from '@/services/api';
import OrderSite from '@/model/OrderSite';
import { CustomError } from '@/model/CustomError';
import { useDispatch } from 'react-redux';
import { setLoading } from '@/redux/loading/loadingSlice';
import { toast } from 'react-toastify';
import useForm from '@/hooks/useForm';
import validators from '@/helpers/validators';
import { unmask, updateMask as updateMaskCash } from '@/helpers/masks/cash';
import { unmask as unmaskCPF, updateMask as updateMaskCPF } from '@/helpers/masks/cpf';
import { unmask as unmaskPhone, updateMask as updateMaskPhone } from '@/helpers/masks/mobilePhone';
import { updateMask as updateMaskZipCode } from '@/helpers/masks/cep';
import {
  updateHolderName,
  updateMaskCard,
  updateMaskCVV,
  validateCVV,
} from '@/helpers/masks/cardNumber';
import { updateMask as expirationDateMask } from '@/helpers/masks/expirationDate';
import Client from '@/model/Client';
import cep from 'cep-promise';
import Payment from '@/model/Payment';
import { getMaximumInstallment } from '@/helpers/payment/installmentFees';

import PaymentType from '@/model/PaymentType';
import OrderResponse from '@/model/OrderResponse';
import PaymentStatus from '@/model/PaymentStatus';
import { Auth3DSResponse, Auth3DSStatus } from '@/features/checkout/types';
import { SessionResponse } from '@/model/SessionResponse';
import { onlyNumbers } from '@/helpers/common';
import CartPayment from '@/model/CartPayment';
import { decrypt, encrypt } from '@/helpers/crypt/crypt';
import { Authenticate3DSRequest } from '@/model/Authenticate3DSRequest';
import { Authenticate3DSError } from '@/model/Authenticate3DSError';
import { SimplePaymentClient } from '@/model/SimplePaymentClient';
import {
  TicketCheckout,
  EventCheckout,
  CheckoutStep,
  FormInputNameClient,
  FormInputNamePayment,
  FormInputNameAddress,
  FormInputNameCreditCard,
} from '../types';
import { SimplePaymentContainer } from './ui';

export const SimplePaymentComponent: React.FC = (): JSX.Element => {
  const { orderId } = useParams();
  const [percent, setPercent] = useState<number>(0);
  const [percentAmount, setPercentAmount] = useState<number>(0);
  const [step, setStep] = useState<CheckoutStep>(CheckoutStep.INFORMATION);
  const [order, setOrder] = useState<OrderSite>();
  const [events, setEvents] = useState<EventCheckout[]>([]);
  const [tickets, setTickets] = useState<TicketCheckout[]>([]);
  const [amountItens, setAmountItens] = useState<number>(0);
  const [payment, setPayment] = useState<Payment>();
  const [auth3DSResponse, setAuth3DSResponse] = useState<Auth3DSResponse>();
  const [auth3DSStatus, setAuth3DSStatus] = useState<Auth3DSStatus>(Auth3DSStatus.NONE);
  const [validating3DS, setValidating3DS] = useState<boolean>(false);
  const [orderResponse, setOrderResponse] = useState<OrderResponse>();

  const dispatch = useDispatch();
  const handleOnFetchOrder = async (): Promise<void> => {
    try {
      dispatch(setLoading(true));
      const { data } = await api.get<OrderSite>(`/simple-payment/order/${orderId}`);
      if (data.items && data.items.length > 0) {
        const newEvents: EventCheckout[] = [];
        const newTickets: TicketCheckout[] = [];
        const payments: Payment[] = [];
        let newAmountItens = 0;

        data.items.forEach(item => {
          const newPayment: Payment = {
            installmentLimit: getMaximumInstallment(item.orderTicket.ticketPayment.websiteSale),
            allowFractionalPayment: item.orderTicket.ticketPayment.allowFractionalPayment,
            allowPaymentBankSlip: item.orderTicket.ticketPayment.allowPaymentBankSlip,
            allowPaymentPIX: item.orderTicket.ticketPayment.allowPaymentPIX,
            allowPaymentCredit: item.orderTicket.ticketPayment.allowPaymentCredit,
            fees: item.orderTicket.ticketPayment.websiteSale,
            allowDiscount: item.orderTicket.ticketPayment.allowDiscountCoupon,
          };
          payments.push(newPayment);
          newAmountItens += 1;
          let ticketName = item.orderTicket.section.name;
          if (item.itemName && !data.orderFractionalRequest) {
            ticketName += ` - Mesa ${item.itemName}`;
          } else if (item.itemName && data.orderFractionalRequest) {
            ticketName += ` - Camarote ${item.itemName}`;
          }
          let foundEvent = false;
          // eslint-disable-next-line no-plusplus
          for (let e = 0; e < newEvents.length; e++) {
            if (newEvents[e].name === item.event.name) {
              foundEvent = true;
              break;
            }
          }
          if (!foundEvent) {
            newEvents.push({
              name: item.event.name,
              image: item.event.imageDetailUrl,
              startDate: item.event.startDate,
              city: item.event.address.city,
              state: item.event.address.state,
            });
          }
          let foundTicket = false;
          // eslint-disable-next-line no-plusplus
          for (let t = 0; t < newTickets.length; t++) {
            if (newTickets[t].name === ticketName) {
              foundTicket = true;
              newTickets[t].amount += 1;
              newTickets[t].total += Number(item.unitValue);
              break;
            }
          }
          if (!foundTicket) {
            newTickets.push({
              id: item.orderTicket.id,
              name: ticketName,
              value: Number(item.unitValue),
              amount: 1,
              total: Number(item.unitValue),
            });
          }
        });
        setEvents(newEvents);
        setTickets(newTickets);
        setAmountItens(newAmountItens);
        if (payments && payments.length > 0) {
          setPayment(payments[0]);
        }
      }
      setOrder(data);
      if (data && data.orderFractionalRequest) {
        setPercentAmount(14);
        setPercent(14);
      } else {
        setPercentAmount(17);
        setPercent(17);
      }
    } catch (error) {
      let messageError = '';
      if (error instanceof AxiosError) {
        const axiosError = error as AxiosError<CustomError, CustomError>;
        if (axiosError.response && axiosError.response.data) {
          if (axiosError.response.data.details) {
            // eslint-disable-next-line no-plusplus
            for (let i = 0; i < axiosError.response.data.details.length; i++) {
              if (i > 0) {
                break;
              }
              messageError = axiosError.response.data.details[i];
            }
          } else if (axiosError.response.data.message) {
            messageError = axiosError.response.data.message;
          }
        }
      } else {
        messageError = 'Erro ao verificar imagem!';
      }
      toast.error(messageError);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const toCurrency = (value: number): string => {
    const formatter = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
    return formatter.format(value);
  };

  const {
    formData: formDataClient,
    formErrors: formErrorsClient,
    onChangeFormInput: onChangeFormInputClient,
    isFormValid: isFormValidClient,
    resetForm: resetFormClient,
  } = useForm({
    initialData: {
      cpf: '',
      email: '',
      phone: '',
    },
    validators: {
      cpf: [validators.required, validators.cpf],
      email: [validators.required, validators.email],
      phone: [validators.required, validators.mobilePhone],
    },
    formatters: {
      cpf: updateMaskCPF,
      phone: updateMaskPhone,
    },
  });

  const {
    formData: formDataAddress,
    formErrors: formErrorsAddress,
    onChangeFormInput: onChangeFormInputAddress,
    isFormValid: isFormValidAddress,
    resetForm: resetFormAddress,
  } = useForm({
    initialData: {
      zipCode: '',
      state: '',
      city: '',
      district: '',
      street: '',
      complement: '',
      number: '',
    },
    validators: {
      zipCode: [validators.required, validators.maxLength(9)],
      state: [validators.required],
      city: [validators.required],
      district: [validators.required],
      street: [validators.required],
      number: [validators.required],
    },
    formatters: {
      zipCode: updateMaskZipCode,
    },
  });

  const {
    formData: formDataPayment,
    formErrors: formErrorsPayment,
    onChangeFormInput: onChangeFormInputPayment,
    isFormValid: isFormValidPayment,
    resetForm: resetFormPayment,
  } = useForm({
    initialData: {
      clientId: '',
      paymentFractionalValue: '',
      paymentValue: '',
      paymentType: '',
      installments: '',
      paymentValueWithFee: '',
    },
    formatters: {
      paymentFractionalValue: updateMaskCash,
    },
  });

  const {
    formData: formDataCreditCard,
    formErrors: formErrorsCreditCard,
    onChangeFormInput: onChangeFormInputCreditCard,
    isFormValid: isFormValidCreditCard,
    setErrors: setErrorsCreditCard,
    resetForm: resetFormCreditCard,
  } = useForm({
    initialData: {
      number: '',
      month: '',
      year: '',
      cvv: '',
      name: '',
      document: '',
    },
    validators: {
      number: [validators.required, validators.cardNumber],
      month: [validators.required],
      year: [validators.required],
      cvv: [validators.required, validators.cvv],
      name: [validators.required],
      document: [validators.required, validators.cpforcnpj],
    },
    formatters: {
      number: updateMaskCard,
      date: expirationDateMask,
      cvv: updateMaskCVV,
      name: updateHolderName,
      document: updateMaskCPF,
    },
  });

  const handleOnCheckCPF = async (cpf: string): Promise<void> => {
    try {
      dispatch(setLoading(true));
      const { data } = await api.get<SimplePaymentClient>(`/simple-payment/client/${cpf}`);
      if (data && data.id) {
        onChangeFormInputClient(FormInputNameClient.cpf)(data.cpf);
        onChangeFormInputClient(FormInputNameClient.email)(data.email);
        onChangeFormInputClient(FormInputNameClient.phone)(data.phone);
        if (data.address) {
          onChangeFormInputAddress(FormInputNameAddress.zipCode)(data.address.zipCode);
          onChangeFormInputAddress(FormInputNameAddress.state)(data.address.state);
          onChangeFormInputAddress(FormInputNameAddress.city)(data.address.city);
          onChangeFormInputAddress(FormInputNameAddress.district)(data.address.district);
          onChangeFormInputAddress(FormInputNameAddress.street)(data.address.street);
          if (data.address.number) {
            onChangeFormInputAddress(FormInputNameAddress.number)(data.address.number);
          }
          if (data.address.complement) {
            onChangeFormInputAddress(FormInputNameAddress.complement)(data.address.complement);
          }
        }
        onChangeFormInputPayment(FormInputNamePayment.clientId)(data.id);
      }
    } catch (error) {
      let messageError = '';
      if (error instanceof AxiosError) {
        const axiosError = error as AxiosError<CustomError, CustomError>;
        if (axiosError.response && axiosError.response.data) {
          if (axiosError.response.data.details) {
            // eslint-disable-next-line no-plusplus
            for (let i = 0; i < axiosError.response.data.details.length; i++) {
              if (i > 0) {
                break;
              }
              messageError = axiosError.response.data.details[i];
            }
          } else if (axiosError.response.data.message) {
            messageError = axiosError.response.data.message;
          }
        }
      } else {
        messageError = 'Erro ao verificar imagem!';
      }
      toast.error(messageError);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleOnSaveClient = async (): Promise<void> => {
    if (isFormValidClient()) {
      try {
        dispatch(setLoading(true));
        const payload: Client = {
          cpf: unmaskCPF(formDataClient[FormInputNameClient.cpf]),
          email: formDataClient[FormInputNameClient.email],
          cellPhone: unmaskPhone(formDataClient[FormInputNameClient.phone]),
        } as Client;
        const { data } = await api.post<Client>(`/simple-payment/client`, payload);
        onChangeFormInputPayment(FormInputNamePayment.clientId)(data.id);
        setPercent(percent + percentAmount);
        setStep(step + 1);
      } catch (error) {
        let messageError = '';
        if (error instanceof AxiosError) {
          const axiosError = error as AxiosError<CustomError, CustomError>;
          if (axiosError.response && axiosError.response.data) {
            if (axiosError.response.data.details) {
              // eslint-disable-next-line no-plusplus
              for (let i = 0; i < axiosError.response.data.details.length; i++) {
                if (i > 0) {
                  break;
                }
                messageError = axiosError.response.data.details[i];
              }
            } else if (axiosError.response.data.message) {
              messageError = axiosError.response.data.message;
            }
          }
        } else {
          messageError = 'Erro ao verificar imagem!';
        }
        toast.error(messageError);
      } finally {
        dispatch(setLoading(false));
      }
    }
  };

  const handleOnChangeCep = async (value: string): Promise<void> => {
    if (value.length === 9) {
      const cepResponse = await cep(value);
      onChangeFormInputAddress(FormInputNameAddress.state)(cepResponse.state);
      onChangeFormInputAddress(FormInputNameAddress.city)(cepResponse.city);
      onChangeFormInputAddress(FormInputNameAddress.district)(cepResponse.neighborhood);
      onChangeFormInputAddress(FormInputNameAddress.street)(cepResponse.street);
    }
  };

  const handleOnSaveAddress = async (): Promise<void> => {
    if (isFormValidAddress()) {
      try {
        dispatch(setLoading(true));
        const payload: Client = {
          id: formDataPayment[FormInputNamePayment.clientId],
          address: {
            zipCode: formDataAddress[FormInputNameAddress.zipCode],
            state: formDataAddress[FormInputNameAddress.state],
            city: formDataAddress[FormInputNameAddress.city],
            district: formDataAddress[FormInputNameAddress.district],
            street: formDataAddress[FormInputNameAddress.street],
            complement: formDataAddress[FormInputNameAddress.complement]
              ? formDataAddress[FormInputNameAddress.complement]
              : undefined,
            number: formDataAddress[FormInputNameAddress.number],
          },
        } as Client;
        await api.post(`/simple-payment/address`, payload);
        setPercent(percent + percentAmount);
        if (order) {
          if (order && order.orderFractionalRequest) {
            setStep(step + 1);
          } else {
            setStep(step + 2);
            onChangeFormInputPayment(FormInputNamePayment.paymentValue)(`${order.totalValue}`);
          }
        }
      } catch (error) {
        let messageError = '';
        if (error instanceof AxiosError) {
          const axiosError = error as AxiosError<CustomError, CustomError>;
          if (axiosError.response && axiosError.response.data) {
            if (axiosError.response.data.details) {
              // eslint-disable-next-line no-plusplus
              for (let i = 0; i < axiosError.response.data.details.length; i++) {
                if (i > 0) {
                  break;
                }
                messageError = axiosError.response.data.details[i];
              }
            } else if (axiosError.response.data.message) {
              messageError = axiosError.response.data.message;
            }
          }
        } else {
          messageError = 'Erro ao verificar imagem!';
        }
        toast.error(messageError);
      } finally {
        dispatch(setLoading(false));
      }
    }
  };

  const handleOnFinished = (): void => {
    resetFormClient();
    resetFormAddress();
    resetFormPayment();
    resetFormCreditCard();
    setPercent(100);
    setStep(CheckoutStep.FINISHED);
  };

  const connectToWebSocket = (selectedOrderId: string): void => {
    const socket = new WebSocket(
      `wss://api.bancadoingresso.com.br/api/websocket/v1/pix/${selectedOrderId}`,
    );
    socket.onopen = (): void => {
      // eslint-disable-next-line no-console
      console.log('Connected to the web socket');
    };
    socket.onmessage = (m: MessageEvent<string>) => {
      if (m.data === 'APPROVED') {
        const newOrderResponse: OrderResponse = {
          orderId: selectedOrderId,
          paymentType: PaymentType.PIX,
          paymentStatus: PaymentStatus.APPROVED,
          fileURL: '',
          content: '',
          paymentMessage: '',
        };
        setOrderResponse(newOrderResponse);
        handleOnFinished();
      }
    };
    socket.onclose = () => {
      connectToWebSocket(selectedOrderId);
    };
    socket.onerror = () => {
      connectToWebSocket(selectedOrderId);
    };
  };

  const create3DSAuthRequest = async (): Promise<void> => {
    try {
      dispatch(setLoading(true));
      setAuth3DSResponse(undefined);
      if (order) {
        const value = Number(formDataPayment[FormInputNamePayment.paymentValueWithFee]);

        const valueString = onlyNumbers(value.toFixed(2));
        const paymentOriginalValue = Number(formDataPayment[FormInputNamePayment.paymentValue]);
        const paymentValue = Number(valueString);

        let paymentFee = 0;
        if (order.orderFractionalRequest) {
          paymentFee = (value * 100) / paymentOriginalValue / 100;
        }

        const card = {
          number: onlyNumbers(formDataCreditCard[FormInputNameCreditCard.number]),
          month: formDataCreditCard[FormInputNameCreditCard.month],
          year: formDataCreditCard[FormInputNameCreditCard.year],
          cvv: formDataCreditCard[FormInputNameCreditCard.cvv],
          name: formDataCreditCard[FormInputNameCreditCard.name],
          document: formDataCreditCard[FormInputNameCreditCard.document],
        };

        const payload: CartPayment = {
          paymentType: PaymentType.CREDIT_CARD,
          installments: Number(Number(formDataPayment[FormInputNamePayment.installments])),
          paymentOriginalValue,
          paymentValue,
          paymentFee,
          card: encrypt(JSON.stringify(card)),
        };
        const { data } = await api.post<SessionResponse>(
          `/simple-payment/order/${order.id}/client/${formDataPayment[FormInputNamePayment.clientId]}/session`,
          payload,
        );
        const request = JSON.parse(decrypt(data.requests)) as Authenticate3DSRequest;
        const newAuth3DSResponse: Auth3DSResponse = {
          cardId: '',
          session: data.session,
          installments: payload.installments,
          paymentOriginalValue: payload.paymentOriginalValue,
          paymentValue: payload.paymentValue,
          paymentFee: payload.paymentFee,
          request,
        };
        setAuth3DSResponse(newAuth3DSResponse);
        setAuth3DSStatus(Auth3DSStatus.START);
      }
    } catch (error) {
      let messageError = '';
      if (error instanceof AxiosError) {
        const axiosError = error as AxiosError<CustomError, CustomError>;
        if (axiosError.response && axiosError.response.data) {
          if (axiosError.response.data.details) {
            // eslint-disable-next-line no-plusplus
            for (let i = 0; i < axiosError.response.data.details.length; i++) {
              if (i > 0) {
                break;
              }
              messageError = axiosError.response.data.details[i];
            }
          } else if (axiosError.response.data.message) {
            messageError = axiosError.response.data.message;
          }
        }
      } else {
        messageError = 'Erro ao verificar imagem!';
      }
      const newOrderResponse: OrderResponse = {
        orderId: order ? order.id : '',
        paymentType: PaymentType.CREDIT_CARD,
        paymentStatus: PaymentStatus.DISAPPROVED,
        paymentMessage: messageError,
        fileURL: '',
        content: '',
      };
      setStep(CheckoutStep.FINISHED);
      setPercent(100);
      setOrderResponse(newOrderResponse);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const create3DSAuth = async (selectedAuth3DSResponse: Auth3DSResponse): Promise<void> => {
    setValidating3DS(true);
    const { REACT_APP_PAGSEGURO_ENV } = process.env;
    PagSeguro.setUp({
      session: selectedAuth3DSResponse.session,
      env: REACT_APP_PAGSEGURO_ENV as 'PROD' | 'SANDBOX',
    });
    try {
      dispatch(setLoading(false));
      const response = await PagSeguro.authenticate3DS(selectedAuth3DSResponse.request);
      const newAuth3DSResponse = {
        ...selectedAuth3DSResponse,
        response,
      };

      setAuth3DSResponse(newAuth3DSResponse);
      setAuth3DSStatus(Auth3DSStatus.AUTH_SUCCESS);
    } catch (error) {
      const newError = error as Authenticate3DSError;
      const newAuth3DSResponse = {
        ...selectedAuth3DSResponse,
        error: newError,
      };

      setAuth3DSResponse(newAuth3DSResponse);
      setAuth3DSStatus(Auth3DSStatus.AUTH_ERROR);
    }
  };

  const handleOnSendPayment = async (cartPayment: CartPayment): Promise<OrderResponse> => {
    try {
      dispatch(setLoading(true));
      const { data } = await api.post<OrderResponse>(
        `/simple-payment/order/${order?.id}/client/${formDataPayment[FormInputNamePayment.clientId]}/payment`,
        cartPayment,
      );
      return data;
    } catch (error) {
      let messageError = '';
      if (error instanceof AxiosError) {
        const axiosError = error as AxiosError<CustomError, CustomError>;
        if (axiosError.response && axiosError.response.data) {
          if (axiosError.response.data.details) {
            // eslint-disable-next-line no-plusplus
            for (let i = 0; i < axiosError.response.data.details.length; i++) {
              if (i > 0) {
                break;
              }
              messageError = axiosError.response.data.details[i];
            }
          } else if (axiosError.response.data.message) {
            messageError = axiosError.response.data.message;
          }
        }
      } else {
        messageError = 'Erro ao solicitar o pagamento!';
      }
      const newOrderResponse: OrderResponse = {
        orderId: order ? order.id : '',
        paymentType: cartPayment.paymentType,
        paymentStatus: PaymentStatus.DISAPPROVED,
        paymentMessage: messageError,
        fileURL: '',
        content: '',
      };
      return newOrderResponse;
    } finally {
      dispatch(setLoading(false));
    }
  };

  const checkAuth3DS = async (): Promise<void> => {
    let message: string | undefined;
    if (auth3DSResponse && auth3DSResponse.response) {
      if (auth3DSResponse.response.authenticationStatus === 'NOT_AUTHENTICATED') {
        message = 'Valicação 3DS não autenticada!';
      } else if (auth3DSResponse.response.status === 'CHANGE_PAYMENT_METHOD') {
        message = 'Validação 3DS solicitou alterar a forma de pagamento!';
      }
    }
    if (message) {
      const newOrderResponse: OrderResponse = {
        orderId: order ? order.id : '',
        paymentType: PaymentType.CREDIT_CARD,
        paymentStatus: PaymentStatus.DISAPPROVED,
        paymentMessage: message,
        fileURL: '',
        content: '',
      };
      setValidating3DS(false);
      setStep(CheckoutStep.FINISHED);
      setPercent(100);
      setOrderResponse(newOrderResponse);
    } else if (auth3DSResponse && !auth3DSResponse.response) {
      create3DSAuth(auth3DSResponse);
    } else if (auth3DSResponse) {
      setValidating3DS(false);
      const { REACT_APP_PAGSEGURO_TOKEN } = process.env;
      const card = PagSeguro.encryptCard({
        publicKey: REACT_APP_PAGSEGURO_TOKEN,
        holder: formDataCreditCard[FormInputNameCreditCard.name],
        number: onlyNumbers(formDataCreditCard[FormInputNameCreditCard.number]),
        expMonth: formDataCreditCard[FormInputNameCreditCard.month],
        expYear: formDataCreditCard[FormInputNameCreditCard.year],
        securityCode: formDataCreditCard[FormInputNameCreditCard.cvv],
      });
      const encrypted = card.encryptedCard;
      const cardPayment: CartPayment = {
        paymentType: PaymentType.CREDIT_CARD,
        installments: Number(auth3DSResponse.installments),
        clientCardId: auth3DSResponse.cardId,
        encrypted,
        authenticationId: auth3DSResponse.response ? auth3DSResponse.response?.id : undefined,
        paymentOriginalValue: auth3DSResponse.paymentOriginalValue,
        paymentValue: auth3DSResponse.paymentValue / 100,
        paymentFee: auth3DSResponse.paymentFee,
      };
      const newOrderResponse = await handleOnSendPayment(cardPayment);
      setStep(CheckoutStep.FINISHED);
      setPercent(100);
      setOrderResponse(newOrderResponse);
    }
  };

  const handleOnStartPayment = async (): Promise<void> => {
    if (isFormValidPayment()) {
      if (order) {
        const paymentType = Number(formDataPayment[FormInputNamePayment.paymentType]);
        if (paymentType === PaymentType.CREDIT_CARD) {
          create3DSAuthRequest();
          /*
          const newOrderResponse: OrderResponse = {
            orderId: order.id,
            paymentType: PaymentType.CREDIT_CARD,
            paymentStatus: PaymentStatus.DISAPPROVED,
            fileURL: '',
            content: '',
            paymentMessage: 'Erro ao processar seu pagamento, não tente novamente',
          };
          setOrderResponse(newOrderResponse);
          handleOnFinished();
          */
        } else if (paymentType === PaymentType.PIX) {
          const value = Number(formDataPayment[FormInputNamePayment.paymentValueWithFee]);
          const valueString = onlyNumbers(value.toFixed(2));
          const paymentOriginalValue = Number(formDataPayment[FormInputNamePayment.paymentValue]);
          const paymentValue = Number(valueString);

          let paymentFee = 0;
          if (order.orderFractionalRequest) {
            paymentFee = (value * 100) / paymentOriginalValue / 100;
          }
          const cardPayment: CartPayment = {
            paymentType,
            installments: 0,
            paymentOriginalValue,
            paymentValue,
            paymentFee,
          };
          const newOrderResponse = await handleOnSendPayment(cardPayment);
          setOrderResponse(newOrderResponse);
          connectToWebSocket(order.id);
        } else if (paymentType === PaymentType.BANK_SLIP) {
          const value = Number(formDataPayment[FormInputNamePayment.paymentValueWithFee]);
          const valueString = onlyNumbers(value.toFixed(2));
          const paymentOriginalValue = Number(formDataPayment[FormInputNamePayment.paymentValue]);
          const paymentValue = Number(valueString);

          let paymentFee = 0;
          if (order.orderFractionalRequest) {
            paymentFee = (value * 100) / paymentOriginalValue / 100;
          }
          const cardPayment: CartPayment = {
            paymentType,
            installments: 0,
            paymentOriginalValue,
            paymentValue,
            paymentFee,
          };
          const newOrderResponse = await handleOnSendPayment(cardPayment);
          setOrderResponse(newOrderResponse);
          resetFormClient();
          resetFormAddress();
          resetFormCreditCard();
          setPercent(100);
          setOrderResponse(newOrderResponse);
        }
      }
    }
  };

  const handleOnCreateCreditCardPayment = (): void => {
    if (isFormValidCreditCard()) {
      if (
        validateCVV(
          formDataCreditCard[FormInputNameCreditCard.number],
          formDataCreditCard[FormInputNameCreditCard.cvv],
        )
      ) {
        handleOnStartPayment();
      } else {
        setErrorsCreditCard({ cvv: ['CVV Inválido'] });
      }
    }
  };

  const handleOnGoToNextStep = async (): Promise<void> => {
    if (step === CheckoutStep.INFORMATION) {
      setStep(step + 1);
      setPercent(percent + percentAmount);
    } else if (step === CheckoutStep.CLIENT_DATA) {
      await handleOnSaveClient();
    } else if (step === CheckoutStep.ADDRESS) {
      await handleOnSaveAddress();
    } else if (step === CheckoutStep.PAYMENT_VALUE) {
      if (
        !formDataPayment[FormInputNamePayment.paymentFractionalValue] ||
        !Number(unmask(formDataPayment[FormInputNamePayment.paymentFractionalValue])) ||
        Number(unmask(formDataPayment[FormInputNamePayment.paymentFractionalValue])) === 0
      ) {
        toast.warn('Infomar o valor a ser pago!');
      } else {
        onChangeFormInputPayment(FormInputNamePayment.paymentValue)(
          unmask(formDataPayment[FormInputNamePayment.paymentFractionalValue]),
        );
        setStep(CheckoutStep.PAYMENT_CHOICE);
        setPercent(percent + percentAmount);
      }
    } else if (step === CheckoutStep.PAYMENT_CHOICE) {
      if (order) {
        const paymentType = Number(formDataPayment[FormInputNamePayment.paymentType]);
        if (paymentType === PaymentType.PIX || paymentType === PaymentType.BANK_SLIP) {
          await handleOnStartPayment();
          setStep(CheckoutStep.PAYMENT);
        } else if (!formDataPayment[FormInputNamePayment.installments]) {
          toast.warn('Infome a quantidade de parcelas!');
        } else {
          setStep(CheckoutStep.PAYMENT);
        }
      }
    } else if (step === CheckoutStep.PAYMENT) {
      if (order) {
        const paymentType = Number(formDataPayment[FormInputNamePayment.paymentType]);
        if (paymentType === PaymentType.CREDIT_CARD) {
          await handleOnCreateCreditCardPayment();
        }
      }
    }
  };

  const handleCopyText = (text: string): void => {
    navigator.clipboard.writeText(text);
    toast.info('Conteudo copiado com sucesso!');
  };

  const handleOnSupportClick = (): void => {
    window.open('https://suporte.bancadoingresso.com.br/open', '_blank');
  };

  useEffect(() => {
    if (auth3DSStatus === Auth3DSStatus.START && auth3DSResponse) {
      create3DSAuth(auth3DSResponse);
    } else if (auth3DSStatus === Auth3DSStatus.AUTH_SUCCESS && auth3DSResponse) {
      let check = false;

      if (auth3DSResponse.response) {
        check = true;
      }

      if (check) {
        setTimeout(() => {
          checkAuth3DS();
        }, 300);
      }
    } else if (auth3DSStatus === Auth3DSStatus.AUTH_ERROR) {
      const newOrderResponse: OrderResponse = {
        orderId: order ? order.id : '',
        paymentType: PaymentType.CREDIT_CARD,
        paymentStatus: PaymentStatus.DISAPPROVED,
        paymentMessage: 'Erro ao criar sessão!',
        fileURL: '',
        content: '',
      };
      setValidating3DS(false);
      setStep(CheckoutStep.FINISHED);
      setPercent(100);
      setOrderResponse(newOrderResponse);
    }
  }, [auth3DSStatus, auth3DSResponse]);

  useEffect(() => {
    handleOnFetchOrder();
    setOrderResponse(undefined);
  }, [orderId]);

  return (
    <SimplePaymentContainer
      step={step}
      order={order}
      events={events}
      tickets={tickets}
      amountItens={amountItens}
      toCurrency={toCurrency}
      onGoToNextStep={handleOnGoToNextStep}
      formDataClient={formDataClient}
      formErrorsClient={formErrorsClient}
      onChangeFormInputClient={onChangeFormInputClient}
      onCheckCPF={handleOnCheckCPF}
      formDataAddress={formDataAddress}
      formErrorsAddress={formErrorsAddress}
      onChangeFormInputAddress={onChangeFormInputAddress}
      onChangeCEP={handleOnChangeCep}
      payment={payment}
      formDataPayment={formDataPayment}
      onChangeFormInputPayment={onChangeFormInputPayment}
      formErrorsPayment={formErrorsPayment}
      validating3DS={validating3DS}
      formDataCreditCard={formDataCreditCard}
      onChangeFormInputCreditCard={onChangeFormInputCreditCard}
      formErrorsCreditCard={formErrorsCreditCard}
      orderResponse={orderResponse}
      onCopyText={handleCopyText}
      onTryAgain={handleOnStartPayment}
      onSupportClick={handleOnSupportClick}
      percent={percent}
    />
  );
};
